import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, KeyRound, LoaderCircle, Save } from "lucide-react";
import { useEffect, useState } from "react";

import { api, type AccessList } from "@/client";

type Operator = AccessList[number];

export function AccessView() {
	const query = useQuery({
		queryKey: ["access"],
		queryFn: () => api.access.list(),
	});

	if (query.isPending) {
		return (
			<div className="flex items-center gap-3 py-12 text-sm font-medium text-stone-600">
				<LoaderCircle className="size-5 animate-spin" />
				Abrindo os acessos…
			</div>
		);
	}
	if (query.error) {
		return (
			<p className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800">
				{query.error.message}
			</p>
		);
	}

	return (
		<section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
			<div className="border-b border-stone-200 px-5 py-4">
				<p className="text-sm font-semibold text-stone-950">Pessoas autorizadas</p>
				<p className="mt-1 text-xs leading-5 text-stone-500">
					O vínculo com o Google é criado no primeiro acesso. Depois disso, o e-mail fica protegido;
					desative a pessoa para encerrar todas as sessões.
				</p>
			</div>

			<div className="divide-y divide-stone-100">
				{query.data.map((operator) => (
					<OperatorRow key={operator.id} operator={operator} />
				))}
			</div>
		</section>
	);
}

function OperatorRow({ operator }: { operator: Operator }) {
	const queryClient = useQueryClient();
	const [email, setEmail] = useState(operator.email ?? "");
	const [role, setRole] = useState<"ADMIN" | "OPERATOR">(
		operator.role === "ADMIN" ? "ADMIN" : "OPERATOR",
	);
	const [active, setActive] = useState(operator.active);
	const mutation = useMutation({
		mutationFn: () =>
			api.access.update({
				id: operator.id,
				email: email.trim() || null,
				role,
				active,
			}),
		onSuccess: (operators) => {
			queryClient.setQueryData(["access"], operators);
		},
	});

	useEffect(() => {
		setEmail(operator.email ?? "");
		setRole(operator.role === "ADMIN" ? "ADMIN" : "OPERATOR");
		setActive(operator.active);
	}, [operator]);

	return (
		<div className="grid gap-4 px-5 py-4 lg:grid-cols-[minmax(180px,0.8fr)_minmax(260px,1.2fr)_150px_130px_auto] lg:items-end">
			<div>
				<p className="text-sm font-semibold text-stone-950">{operator.name}</p>
				<p className="mt-1 flex items-center gap-1.5 text-xs text-stone-500">
					{operator.auth_user_id ? (
						<>
							<CircleCheck className="size-3.5 text-emerald-600" />
							Conta Google vinculada
						</>
					) : (
						<>
							<KeyRound className="size-3.5" />
							Aguardando primeiro acesso
						</>
					)}
				</p>
			</div>

			<label className="text-xs font-medium text-stone-600">
				E-mail Google
				<input
					type="email"
					value={email}
					onChange={(event) => setEmail(event.target.value)}
					disabled={!!operator.auth_user_id}
					placeholder="pessoa@sindi.ai"
					className="mt-1 block h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm disabled:bg-stone-50 disabled:text-stone-500"
				/>
			</label>

			<label className="text-xs font-medium text-stone-600">
				Papel
				<select
					value={role}
					onChange={(event) => setRole(event.target.value === "ADMIN" ? "ADMIN" : "OPERATOR")}
					className="mt-1 block h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm"
				>
					<option value="OPERATOR">Operador</option>
					<option value="ADMIN">Administrador</option>
				</select>
			</label>

			<label className="flex h-10 items-center gap-2 text-sm font-medium text-stone-700">
				<input
					type="checkbox"
					checked={active}
					onChange={(event) => setActive(event.target.checked)}
					className="size-4"
				/>
				Acesso ativo
			</label>

			<button
				type="button"
				onClick={() => mutation.mutate()}
				disabled={mutation.isPending}
				className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-950 px-4 text-sm font-semibold text-white disabled:opacity-60"
			>
				{mutation.isPending ? (
					<LoaderCircle className="size-4 animate-spin" />
				) : (
					<Save className="size-4" />
				)}
				Salvar
			</button>

			{mutation.error && (
				<p className="text-xs text-red-700 lg:col-start-2 lg:col-end-6">{mutation.error.message}</p>
			)}
		</div>
	);
}
